import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, MapPin, Search, ArrowRight, BookOpen } from "lucide-react";
import api from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";

const HERO = "https://static.prod-images.emergentagent.com/jobs/50f287ae-e19f-418f-b7ac-9951f0f22da5/images/10109dacf553dfb47cb3993579e2b4ff429cc192b507ff5f3bc0713789659953.jpeg";

const WORLD = [
  { label: "Oil Massage", to: "/therapies/abhyanga", img: "https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { label: "Ayurveda Retreats", to: "/therapies/panchakarma", img: "https://images.unsplash.com/photo-1609828913552-f9138ed9e42d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { label: "Yoga & Daily Living", to: "/tags/seasonal-wellness", img: "https://images.unsplash.com/photo-1522075782449-e45a34f1ddfb?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { label: "Herbs & Traditions", to: "/herbs", img: "https://images.unsplash.com/photo-1551772413-6c1b7dc18548?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
];

const TREATMENTS = [
  { label: "Abhyanga (Oil Massage)", slug: "abhyanga" },
  { label: "Shirodhara", slug: "shirodhara" },
  { label: "Panchakarma", slug: "panchakarma" },
  { label: "Nasya", slug: "nasya" },
];
const LOCATIONS = ["Kochi", "Munnar", "Alappuzha", "Thiruvananthapuram", "Wayanad"];
const NETWORK = ["KeralaHealth.com", "KeralaTravels.com", "Kerala.com", "Indias.com"];

const GOLD = "#C9A24B";

export default function HomeLanding() {
  const navigate = useNavigate();
  const [treatment, setTreatment] = useState("");
  const [location, setLocation] = useState("");

  const { data: articles = [] } = useQuery({
    queryKey: ["articles"],
    queryFn: () => api.get("/articles").then((r) => r.data),
  });

  const discover = () => {
    if (treatment) navigate(`/therapies/${treatment}`);
    else navigate("/therapies");
  };

  return (
    <div className="bg-brand-deep">
      {/* HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Ayurvedic oil therapy in Kerala" className="h-full w-full object-cover object-right" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-deep via-brand-deep/95 to-brand-deep/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-deep via-transparent to-transparent" />
        </div>

        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-16 pb-10 lg:pt-24 lg:pb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-xl"
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-brand-sand">
              A Tradition of Care.
              <br />
              <span style={{ color: GOLD }}>A Moment for Yourself.</span>
            </h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-brand-sand/80">
              Explore Ayurveda, traditional oil therapies and thoughtfully planned wellness stays in Kerala.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                data-testid="hero-explore-ayurveda"
                to="/about"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-medium text-brand-deep transition-transform hover:scale-[1.02]"
                style={{ background: GOLD }}
              >
                Explore Ayurveda <Leaf className="h-4 w-4" />
              </Link>
              <Link
                data-testid="hero-find-centre"
                to="/therapies"
                className="inline-flex items-center gap-2 rounded-full border border-brand-sand/40 px-6 py-3.5 font-medium text-brand-sand backdrop-blur-sm transition-colors hover:bg-brand-sand/10"
              >
                <MapPin className="h-4 w-4" /> Find a Wellness Centre
              </Link>
            </div>
          </motion.div>

          {/* Selector bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 grid gap-4 rounded-2xl bg-brand-sand/95 p-4 shadow-2xl backdrop-blur sm:grid-cols-[1fr_1fr_auto] sm:items-end"
          >
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-muted">
                <Leaf className="h-3.5 w-3.5" style={{ color: GOLD }} /> Explore treatments
              </label>
              <select
                data-testid="treatment-select"
                value={treatment}
                onChange={(e) => setTreatment(e.target.value)}
                className="w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm outline-none focus:border-brand"
              >
                <option value="">Select a treatment</option>
                {TREATMENTS.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-muted">
                <MapPin className="h-3.5 w-3.5" style={{ color: GOLD }} /> Choose a location
              </label>
              <select
                data-testid="location-select"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm outline-none focus:border-brand"
              >
                <option value="">Select a location</option>
                {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <button
              data-testid="discover-btn"
              onClick={discover}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand px-6 py-2.5 font-medium text-brand-sand transition-transform hover:scale-[1.02]"
            >
              <Search className="h-4 w-4" /> Discover
            </button>
          </motion.div>
        </div>
      </section>

      {/* EXPLORE THE WORLD OF AYURVEDA */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-16">
        <div className="flex flex-col items-center">
          <h2 className="font-heading text-3xl sm:text-4xl text-brand-sand">Explore the World of Ayurveda</h2>
          <span className="mt-3 inline-block h-px w-24" style={{ background: GOLD }} />
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {WORLD.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
            >
              <Link to={c.to} data-testid={`world-card-${c.to.replace(/\//g, "-")}`} className="group relative block overflow-hidden rounded-xl border" style={{ borderColor: "rgba(201,162,75,0.35)" }}>
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={c.img} alt={c.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-deep via-brand-deep/70 to-transparent p-4 pt-12 text-center">
                  <Leaf className="mx-auto h-4 w-4" style={{ color: GOLD }} />
                  <p className="mt-1 font-heading text-lg text-brand-sand">{c.label}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link data-testid="read-guides" to="/articles" className="inline-flex items-center gap-2 font-medium" style={{ color: GOLD }}>
            <BookOpen className="h-4 w-4" /> Read our Ayurveda guides <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* FROM THE JOURNAL — imported Ayurvedam.com articles */}
      <section className="bg-brand-sand py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-brand-clay">From the Ayurvedam Journal</span>
              <h2 className="mt-2 font-heading text-3xl sm:text-4xl text-brand-ink">Reading on Ayurveda</h2>
              <p className="mt-2 text-brand-muted">Articles from Ayurvedam.com on therapies, herbs, wellness and Kerala's living tradition.</p>
            </div>
            <Link to="/articles" data-testid="see-all-journal" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:gap-2.5 transition-all">
              All articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {articles.slice(0, 6).map((a, i) => (
              <div data-testid={`home-article-${a.slug}`} key={a.slug}>
                <ArticleCard article={a} index={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONNECTED WITH */}
      <section data-testid="network-strip" className="border-t border-white/10 bg-brand-deep py-10">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 text-center">
          <p className="text-sm text-brand-sand/70">
            <span className="font-medium text-brand-sand">Connected with</span>
            <span className="mx-3 hidden sm:inline">·</span>
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {NETWORK.map((n) => (
              <Link key={n} to="/network" className="text-sm font-medium text-brand-sand/85 transition-colors hover:text-white">
                {n}
              </Link>
            ))}
          </div>
          <p className="mt-6 flex items-center justify-center gap-2 text-xs text-brand-sand/50">
            <Leaf className="h-3.5 w-3.5" style={{ color: GOLD }} />
            Treatment suitability requires assessment by a qualified practitioner.
          </p>
        </div>
      </section>
    </div>
  );
}
